import { ref, nextTick, onMounted, onBeforeUnmount } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import { useMenuRoot } from './useMenuRoot'

interface NavOptions {
  activeMenuItemIndex: Ref<number | null>
  showSkipLink: Ref<boolean>
  tabCount: Ref<number>
  profileCardIsOpen: ComputedRef<boolean>
}

type Direction = 'left' | 'right' | 'up' | 'down'

/**
 * DOM-driven keyboard navigation for the menu.
 *
 * Rather than maintaining a static ID→ID navigation map, navigation is derived
 * from the actual DOM at keypress time using two strategies:
 *
 *  - Menubar items  → DOM order (left/right = prev/next visible focusable in the bar)
 *  - Dropdown grids → Spatial navigation via getBoundingClientRect (nearest element
 *                     in the pressed direction)
 *  - Profile card   → DOM order (vertical list); spatial left/right for theme toggles
 *
 * To wire a region into navigation, add a `data-nav-zone` attribute to its container:
 *   data-nav-zone="menubar"   — the <ul role="menubar">
 *   data-nav-zone="dropdown"  — any open dropdown content body
 *   data-nav-zone="profile"   — the profile card container
 *
 * Focusable elements within a zone must carry the `.wad4-menu-v2__focusable` class.
 * Hidden elements (offsetParent === null) are automatically excluded.
 */
export function useMenuNav(opts: NavOptions) {
  const isMobileView = ref(false)
  const { getElementById, getActiveElement, getRoot } = useMenuRoot()

  // ─── Zone helpers ─────────────────────────────────────────────────────────────

  function getZone(el: HTMLElement): string | null {
    return el.closest('[data-nav-zone]')?.getAttribute('data-nav-zone') ?? null
  }

  /** All visible .wad4-menu-v2__focusable elements within a named zone, in DOM order.
   *
   *  Only top-level focusables are returned: if an element's ancestor within the same
   *  zone also carries the focusable class (e.g. a <li> that wraps an <a>, both marked
   *  focusable) the nested element is excluded so arrow navigation steps one item at a time.
   */
  function getFocusablesIn(zone: string): HTMLElement[] {
    return Array.from(
      getRoot().querySelectorAll<HTMLElement>(`[data-nav-zone="${zone}"] .wad4-menu-v2__focusable`)
    ).filter(el => {
      if (el.offsetParent === null) return false
      // Walk up; if we hit a focusable ancestor before the zone boundary, skip this element.
      let node = el.parentElement
      while (node) {
        if (node.hasAttribute('data-nav-zone')) break
        if (node.classList.contains('wad4-menu-v2__focusable')) return false
        node = node.parentElement
      }
      return true
    })
  }

  // ─── Spatial navigation ───────────────────────────────────────────────────────
  //
  // Finds the nearest candidate element in `dir` from `from` using center-point
  // distances. Primary-axis distance is weighted 1×, cross-axis 0.3× so that
  // columns/rows stay coherent even when items aren't perfectly aligned.

  function spatialNeighbor(from: HTMLElement, dir: Direction, candidates: HTMLElement[]): HTMLElement | null {
    const fr = from.getBoundingClientRect()
    const fc = { x: fr.left + fr.width / 2, y: fr.top + fr.height / 2 }
    const isHorizontal = dir === 'left' || dir === 'right'

    const eligible = candidates.filter(el => {
      if (el === from) return false
      const r = el.getBoundingClientRect()
      const cx = r.left + r.width / 2
      const cy = r.top + r.height / 2
      if (dir === 'right') return cx > fc.x + 5
      if (dir === 'left')  return cx < fc.x - 5
      if (dir === 'down')  return cy > fc.y + 5
      return cy < fc.y - 5
    })

    return eligible.sort((a, b) => {
      const score = (el: HTMLElement) => {
        const r = el.getBoundingClientRect()
        const cx = r.left + r.width / 2
        const cy = r.top + r.height / 2
        const primary = isHorizontal ? Math.abs(cx - fc.x) : Math.abs(cy - fc.y)
        const cross   = isHorizontal ? Math.abs(cy - fc.y) : Math.abs(cx - fc.x)
        return primary + cross * 0.3
      }
      return score(a) - score(b)
    })[0] ?? null
  }

  // ─── Keyboard handlers ────────────────────────────────────────────────────────

  function burgerMenuKeydown(index: number, event: KeyboardEvent) {
    if (index === 0 && event.shiftKey && event.key === 'Tab') {
      event.preventDefault()
      getElementById('wad4-menu-v2-items__burger')?.focus()
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      opts.activeMenuItemIndex.value = null
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    // ── Tab: skip-link flow ────────────────────────────────────────────────────
    if (event.key === 'Tab') {
      getElementById('wad4MenuV2Item_logo')?.classList.remove('wad4-menu-v2__no-focus-styles')
      const focused = getActiveElement() as HTMLElement
      opts.tabCount.value += 1

      if (focused?.classList.contains('wad4-menu-v2__focusable')) {
        if (opts.tabCount.value === 1 || (event.shiftKey && focused.id === 'wad4MenuV2Item_logo')) {
          opts.tabCount.value = 1
          opts.showSkipLink.value = true
          nextTick(() => {
            const el = getElementById('wad4-menu-v2__skip-to-content')
            el?.classList.add('wad4-menu-v2-items__item-skip-to-content--show')
            setTimeout(() => el?.focus(), 10)
          })
          return
        }
        if (opts.tabCount.value === 2) {
          opts.showSkipLink.value = false
          getElementById('wad4-menu-v2__skip-to-content')?.classList.remove('wad4-menu-v2-items__item-skip-to-content--show')
          nextTick(() => (getElementById('wad4MenuV2Item_logo') as HTMLElement | null)?.focus())
          return
        }
      }
    }

    // ── Escape: close dropdown / profile card, return focus to trigger ─────────
    if (event.key === 'Escape') {
      event.preventDefault()
      const focused = getActiveElement() as HTMLElement
      const zone = getZone(focused)
      const triggerIndex = opts.activeMenuItemIndex.value
      opts.activeMenuItemIndex.value = null

      if (zone === 'dropdown' && triggerIndex !== null) {
        getElementById(`wad4MenuV2Item_i${triggerIndex}`)?.focus()
      } else if (zone === 'profile') {
        const trigger = getElementById('wad4-menu-v2-items__avatar') ?? getElementById('wad4-menu-v2-items__guest')
        trigger?.focus()
      }
      return
    }

    // ── Arrow + Tab: zone-aware navigation ────────────────────────────────────
    const dirMap: Record<string, Direction> = {
      ArrowRight: 'right', ArrowDown: 'down', ArrowLeft: 'left', ArrowUp: 'up',
    }
    const dir: Direction | undefined =
      dirMap[event.key] ?? (event.code === 'Tab' ? (event.shiftKey ? 'left' : 'right') : undefined)

    if (dir) {
      event.preventDefault()
      const focused = getActiveElement() as HTMLElement
      const zone = getZone(focused)

      // Menubar: DOM-order left/right; ArrowDown enters the open dropdown
      if (zone === 'menubar') {
        if (dir === 'down') {
          getFocusablesIn('dropdown')[0]?.focus()
          return
        }
        // Tab (not ArrowRight) on the active dropdown trigger → enter the dropdown
        if (event.key === 'Tab' && !event.shiftKey && opts.activeMenuItemIndex.value !== null) {
          const trigger = getElementById(`wad4MenuV2Item_i${opts.activeMenuItemIndex.value}`)
          if (focused === trigger) {
            getFocusablesIn('dropdown')[0]?.focus()
            return
          }
        }
        const items = getFocusablesIn('menubar')
        const idx = items.indexOf(focused)
        if (dir === 'right') items[idx + 1]?.focus()
        else if (dir === 'left') items[idx - 1]?.focus()
        return
      }

      // Dropdown grid: Tab uses DOM order (column-by-column); arrow keys use spatial navigation
      if (zone === 'dropdown') {
        const items = getFocusablesIn('dropdown')
        if (event.key === 'Tab') {
          const idx = items.indexOf(focused)
          const next = event.shiftKey ? items[idx - 1] : items[idx + 1]
          if (next) {
            next.focus()
          } else {
            // Tab past last item / Shift+Tab before first: return focus to the trigger
            const triggerIndex = opts.activeMenuItemIndex.value
            if (triggerIndex !== null) {
              getElementById(`wad4MenuV2Item_i${triggerIndex}`)?.focus()
            }
          }
          return
        }
        const neighbor = spatialNeighbor(focused, dir, items)
        if (neighbor) {
          neighbor.focus()
        } else if (dir === 'up') {
          const triggerIndex = opts.activeMenuItemIndex.value
          if (triggerIndex !== null) {
            getElementById(`wad4MenuV2Item_i${triggerIndex}`)?.focus()
          }
        }
        return
      }

      // Profile card: spatial left/right for theme toggles; DOM order otherwise
      if (zone === 'profile') {
        const themeIds = new Set([
          'wad4-menu-v2-theme-light', 'wad4-menu-v2-theme-system', 'wad4-menu-v2-theme-dark',
          'wad4-menu-v2-theme-light-guest', 'wad4-menu-v2-theme-system-guest', 'wad4-menu-v2-theme-dark-guest',
        ])
        const items = getFocusablesIn('profile')
        if (themeIds.has(focused.id) && (dir === 'left' || dir === 'right')) {
          const neighbor = spatialNeighbor(focused, dir, items)
          if (neighbor) {
            neighbor.focus()
            return
          }
          // Tab past the last / Shift+Tab before the first theme button: fall through to DOM-order nav
          if (event.key !== 'Tab') return
        }
        const idx = items.indexOf(focused)
        if (dir === 'down' || dir === 'right') items[idx + 1]?.focus()
        else if (dir === 'up' || dir === 'left') items[idx - 1]?.focus()
        return
      }
    }

    // ── Enter: activate the focused element ───────────────────────────────────
    if (event.key === 'Enter') {
      const el = getActiveElement() as HTMLElement
      if (el?.id === 'wad4-menu-v2-items__guest') return
      event.preventDefault()
      if (el?.tagName === 'A' && (el as HTMLAnchorElement).getAttribute('href')) {
        window.location.href = (el as HTMLAnchorElement).getAttribute('href')!
        return
      }
      if (el?.tagName === 'LI') {
        const innerLink = el.querySelector('a[href]') as HTMLAnchorElement | null
        if (innerLink?.href) {
          window.location.href = innerLink.href
          return
        }
      }
      if (typeof (el as unknown as { click?: () => void })?.click === 'function') {
        (el as unknown as { click: () => void }).click()
      }
    }
  }

  function handleWindowResize() {
    isMobileView.value = window.innerWidth < 1280
  }

  onMounted(() => {
    isMobileView.value = window.innerWidth < 1280
    window.addEventListener('resize', handleWindowResize)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleWindowResize)
  })

  return { isMobileView, handleKeydown, burgerMenuKeydown }
}